import {
  GiftCouponRedeemStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  GiftOrderForAdmin,
  GiftOrderItemType,
} from '@shared/api/giftOrder/giftOrder.interface';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import {
  fmtTime,
  redeemStatusLabel,
  resolveGiftItemId,
} from '../components/couponDetail/constants';
import { GiftItemEmbed } from '../components/couponDetail/types';

// ─────────────────────────────────────────────
// 주문 1건(또는 여러 주문 통합)의 "신청자 번호" 목록을 엑셀로 내보낸다.
// 셀 스타일은 커뮤니티 xlsx 미지원이라, 지원 범위(열 너비·행 높이·헤더 고정)로만
// 가독성을 확보한다.
// ─────────────────────────────────────────────

const HEADER_ROW_PT = 28;
const BODY_ROW_PT = 22;

/** 컬럼 폭 자동 조정 (한글 2.2칸/영문 1칸 기준) */
function autoWidth(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const cols: XLSX.ColInfo[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    let max = 8;
    for (let R = range.s.r; R <= range.e.r; R++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell?.v == null) continue;
      let w = 0;
      for (const ch of String(cell.v)) w += ch.charCodeAt(0) > 0x7f ? 2.2 : 1;
      if (w > max) max = w;
    }
    cols.push({ wch: Math.min(Math.ceil(max) + 4, 60) });
  }
  ws['!cols'] = cols;
}

/** 헤더는 높게, 데이터 행은 넉넉히 */
function setRowHeights(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const rows: XLSX.RowInfo[] = [];
  for (let R = range.s.r; R <= range.e.r; R++) {
    rows[R] = { hpt: R === 0 ? HEADER_ROW_PT : BODY_ROW_PT };
  }
  ws['!rows'] = rows;
}

/** 헤더 행(1행) 고정 */
function finishSheet(ws: XLSX.WorkSheet) {
  autoWidth(ws);
  setRowHeights(ws);
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  if (!ws['!views']) ws['!views'] = [];
  (ws['!views'] as Array<Record<string, unknown>>)[0] = { state: 'frozen', ySplit: 1 };
}

/** 헤더명으로 열을 찾아 숫자 셀에 천단위 콤마 서식 적용 */
function applyWonFormat(ws: XLSX.WorkSheet, headerName: string) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  for (let C = range.s.c; C <= range.e.c; C++) {
    const head = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (head?.v !== headerName) continue;
    for (let R = 1; R <= range.e.r; R++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell && typeof cell.v === 'number') cell.z = '#,##0';
    }
  }
}

/**
 * 가운데 4자리 마스킹 — 010-****-5678.
 * 하이픈·자릿수 방어 (11자리 3-4-4, 10자리 3-3-4, 그 외는 뒤 4자리 기준).
 */
const maskMiddlePhone = (raw?: string): string => {
  const d = (raw ?? '').replace(/\D/g, '');
  if (d.length === 11) return `${d.slice(0, 3)}-****-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-****-${d.slice(6)}`;
  if (d.length >= 7) return `${d.slice(0, 3)}-****-${d.slice(-4)}`;
  return raw ?? '';
};

/**
 * 신청자 번호 — "나중에 받기"(recipient) 우선, 재발행/연장 발송분은
 * additional/renewal 수신번호로 폴백한다.
 */
const applicantPhone = (c: GiftCouponSummary): string =>
  c.recipient?.phoneNumber ||
  c.additionalRecipientPhone ||
  c.renewalRecipientPhone ||
  '';

/** 선택상품명 — selectedChoiceUnit이 populate된 경우에만 이름을 반환(미선택/미populate는 빈값) */
const choiceUnitName = (c: GiftCouponSummary): string =>
  c.selectedChoiceUnit && typeof c.selectedChoiceUnit === 'object'
    ? c.selectedChoiceUnit.name ?? ''
    : '';

/** 취소·폐기(번호가 무의미해진 상태)가 아닌 쿠폰 */
const isExportable = (c: GiftCouponSummary): boolean =>
  c.redeemStatus !== GiftCouponRedeemStatus.Canceled &&
  c.redeemStatus !== GiftCouponRedeemStatus.Disposed;

/**
 * 내보내기 대상 — 신청자 번호가 있는 쿠폰 중 취소·폐기를 제외.
 * 나중에 사용하기(Available+번호)·번호 인증(나중에받기)·발송완료(Delivered)를 모두 포함한다.
 * 이 목록이 비면 버튼을 비활성화한다.
 */
export const getDeliveredCouponPhoneTargets = (
  coupons: GiftCouponSummary[],
): GiftCouponSummary[] => coupons.filter((c) => !!applicantPhone(c) && isExportable(c));

/** 미발송 포함(번호 기입 폼) 대상 — 취소·폐기만 제외한 전체 쿠폰 */
export const getPhoneFormTargets = (
  coupons: GiftCouponSummary[],
): GiftCouponSummary[] => coupons.filter(isExportable);

/**
 * 주문 스냅샷(orderDetails.giftItemEmbed)에서 쿠폰형 상품의 이름·판매가 맵 구성.
 * (couponDetail의 giftItemEmbedMap과 동일 기준 — 통합 내보내기용으로 여러 주문 허용)
 */
export const buildGiftItemEmbedMap = (
  orders: Array<GiftOrderForAdmin | undefined>,
): Map<string, GiftItemEmbed> => {
  const map = new Map<string, GiftItemEmbed>();
  for (const order of orders) {
    for (const d of order?.orderDetails ?? []) {
      if (d.type !== GiftOrderItemType.Coupon) continue;
      const gid = resolveGiftItemId(d.giftItem);
      if (!gid || map.has(gid)) continue;
      map.set(gid, {
        name: d.giftItemEmbed.name,
        priceRetail: d.giftItemEmbed.priceRetail,
      });
    }
  }
  return map;
};

/** 쿠폰 판매가 — 구매 당시 동결값(salePriceRetail) 우선, 레거시는 주문 스냅샷 폴백 */
export const couponSalePrice = (
  c: GiftCouponSummary,
  embedMap: Map<string, GiftItemEmbed>,
): number | undefined => c.salePriceRetail ?? embedMap.get(c.giftItem)?.priceRetail;

/** 결제일 오름차순(오래된 주문이 주문1) — 통합폼의 주문 라벨 순서 기준 */
export const sortOrdersByPurchasedAt = (
  orders: GiftOrderForAdmin[],
): GiftOrderForAdmin[] =>
  [...orders].sort(
    (a, b) =>
      new Date(a.paymentDetail.purchasedAt ?? a.createdAt).getTime() -
      new Date(b.paymentDetail.purchasedAt ?? b.createdAt).getTime(),
  );

/** 통합폼 주문 구분 라벨(주문시각 포함) — 엑셀과 업로드 미리보기가 같은 표기를 쓴다 */
export const combinedOrderLabel = (
  order: GiftOrderForAdmin,
  index: number,
): string => {
  const d = new Date(order.paymentDetail.purchasedAt ?? order.createdAt);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `주문${index + 1} (${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())})`;
};

interface PhoneRowFlags {
  /** 상품명·판매가 열 포함 여부 */
  hasItem: boolean;
  hasChoiceUnit: boolean;
  hasMemo: boolean;
}

const buildPhoneRow = (
  c: GiftCouponSummary,
  embedMap: Map<string, GiftItemEmbed>,
  flags: PhoneRowFlags,
): Record<string, string | number> => {
  const row: Record<string, string | number> = {
    // 주문 내 쿠폰 고유 순번(coupon.seq) — 화면의 #1, #2 표기와 동일
    쿠폰순번: c.seq != null ? `#${c.seq}` : '',
    쿠폰번호: c.code,
  };
  if (flags.hasItem) {
    row.상품명 = embedMap.get(c.giftItem)?.name ?? '';
    row.판매가 = couponSalePrice(c, embedMap) ?? '';
  }
  if (flags.hasChoiceUnit) row.선택상품 = choiceUnitName(c);
  const phone = applicantPhone(c);
  // 미발송 건은 수령번호 칸에 기입 유도 마커를 넣어 "채워야 할 곳"이 한눈에 띄게 한다.
  // (셀 배경색은 커뮤니티 xlsx 미지원 — 텍스트 마커로 강조)
  row.수령번호 = phone ? maskMiddlePhone(phone) : '▶ 번호기입요망';
  // 사용가능(Available)인데 번호가 있으면 "나중에 받기" 예약 건이므로 그렇게 표기.
  row.상태 = !phone
    ? '미발송'
    : c.redeemStatus === GiftCouponRedeemStatus.Available
      ? '나중에 받기'
      : redeemStatusLabel[c.redeemStatus];
  row.발송일시 = c.gifticon?.deliveredAt ? fmtTime(c.gifticon.deliveredAt) : '';
  if (flags.hasMemo) row.메모 = c.memo?.trim() ?? '';
  return row;
};

const phoneRowHeader = (flags: PhoneRowFlags): string[] => [
  '쿠폰순번',
  '쿠폰번호',
  ...(flags.hasItem ? ['상품명', '판매가'] : []),
  '수령번호',
  ...(flags.hasChoiceUnit ? ['선택상품'] : []),
  '상태',
  '발송일시',
  ...(flags.hasMemo ? ['메모'] : []),
];

export interface DeliveredPhoneExcelOptions {
  /**
   * 미발송(신청 번호 없는) 쿠폰도 포함해 "번호 기입 폼"으로 내보낸다.
   * 쿠폰 순서는 그대로 두고, 미발송 건의 수령번호 칸에 기입 마커를 넣어
   * 채워서 회신받는 용도. 메모 컬럼도 항상 포함 — 수령인 이름을 적어 회신하면
   * 업로드(phoneFormExcelUpload) 시 번호와 함께 메모로 저장된다.
   */
  includeUnsent?: boolean;
}

/**
 * 주문 1건의 신청자 번호를 엑셀로 내보낸다. 수령번호는 가운데 4자리를 ****로 마스킹.
 * 어떤 상품(상품명·판매가)의 쿠폰인지 함께 표기한다.
 * (프로젝트 표준: 커뮤니티 xlsx + file-saver, 정산 엑셀들과 동일)
 */
export const exportDeliveredCouponPhoneExcel = (
  order: GiftOrderForAdmin | undefined,
  coupons: GiftCouponSummary[],
  options?: DeliveredPhoneExcelOptions,
): void => {
  const includeUnsent = options?.includeUnsent ?? false;
  // 기입 폼도 쿠폰 순서(순번)를 그대로 유지한다 — 미발송 건은 사이사이 마커로 강조.
  const targets = includeUnsent
    ? getPhoneFormTargets(coupons)
    : getDeliveredCouponPhoneTargets(coupons);

  const embedMap = buildGiftItemEmbedMap([order]);
  // 선택상품·메모는 하나라도 값이 있을 때만 컬럼을 넣어 빈 컬럼이 생기지 않게 한다.
  // 단, 기입폼은 수령인 이름을 적어 회신받도록 메모 컬럼을 항상 포함한다.
  const flags: PhoneRowFlags = {
    hasItem: targets.some(
      (c) => !!embedMap.get(c.giftItem)?.name || couponSalePrice(c, embedMap) != null,
    ),
    hasChoiceUnit: targets.some((c) => !!choiceUnitName(c)),
    hasMemo: includeUnsent || targets.some((c) => !!c.memo?.trim()),
  };

  const rows = targets.map((c) => buildPhoneRow(c, embedMap, flags));
  const ws = XLSX.utils.json_to_sheet(rows, { header: phoneRowHeader(flags) });
  finishSheet(ws);
  applyWonFormat(ws, '판매가');
  // 기입 폼은 상태 열 필터로 "미발송"만 쏙 골라볼 수 있게 자동필터를 건다.
  if (includeUnsent) ws['!autofilter'] = { ref: ws['!ref'] ?? 'A1' };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    ws,
    includeUnsent ? '번호 기입폼(미발송 포함)' : '발송완료 번호',
  );

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const ordererName = order?.receiverName ? `_${order.receiverName}` : '';
  saveAs(
    new Blob([out], { type: 'application/octet-stream' }),
    includeUnsent
      ? `발송번호기입폼_미발송포함${ordererName}.xlsx`
      : `발송완료번호${ordererName}.xlsx`,
  );
};

/**
 * 통합 번호 기입폼 — 한 주문자가 여러 번 주문한 경우, 결제완료 주문들의 기입폼을
 * "주문" 구분 열과 함께 한 파일로 합쳐 내보낸다 (주문검색 화면에서 사용).
 * 채워 받은 통합폼은 그대로 통합 업로드(주문검색)하거나, 주문별 업로드에 올려도
 * 다른 주문 건은 자동 제외되므로 쪼갤 필요가 없다.
 */
export const exportCombinedPhoneFormExcel = (orders: GiftOrderForAdmin[]): void => {
  const sorted = sortOrdersByPurchasedAt(orders);
  const embedMap = buildGiftItemEmbedMap(sorted);

  const perOrderTargets = sorted.map((o) =>
    getPhoneFormTargets([...(o.coupons ?? [])].sort((a, b) => a.seq - b.seq)),
  );
  const allTargets = perOrderTargets.flat();
  const flags: PhoneRowFlags = {
    hasItem: allTargets.some(
      (c) => !!embedMap.get(c.giftItem)?.name || couponSalePrice(c, embedMap) != null,
    ),
    hasChoiceUnit: allTargets.some((c) => !!choiceUnitName(c)),
    hasMemo: true, // 기입폼 — 수령인 이름 회신용
  };

  // 주문 그룹 사이 빈 행(스페이서)으로 구분하고, "주문" 열은 그룹 단위 세로 병합.
  // 병합은 표시용일 뿐 업로드 파서는 주문 열 값을 쓰지 않아 무해하다(빈 행도 code
  // 없음으로 스킵). 라벨 값은 그룹 전 행에 넣어 병합 해제 시에도 정보가 유지된다.
  const rows: Array<Record<string, string | number>> = [];
  const merges: XLSX.Range[] = [];
  const spacerRows: number[] = [];
  let r = 1; // 0행은 헤더
  let orderIdx = 0;
  sorted.forEach((order, i) => {
    const targets = perOrderTargets[i] ?? [];
    if (targets.length === 0) return;
    if (rows.length > 0) {
      rows.push({});
      spacerRows.push(r);
      r += 1;
    }
    const label = combinedOrderLabel(order, orderIdx);
    orderIdx += 1;
    const start = r;
    for (const c of targets) {
      rows.push({ 주문: label, ...buildPhoneRow(c, embedMap, flags) });
      r += 1;
    }
    if (r - start > 1) merges.push({ s: { r: start, c: 0 }, e: { r: r - 1, c: 0 } });
  });

  const ws = XLSX.utils.json_to_sheet(rows, {
    header: ['주문', ...phoneRowHeader(flags)],
  });
  finishSheet(ws);
  if (merges.length > 0) ws['!merges'] = merges;
  // 스페이서 행은 낮게 눌러 구분선처럼 보이게 한다
  const wsRows = (ws['!rows'] ?? []) as XLSX.RowInfo[];
  for (const sr of spacerRows) wsRows[sr] = { hpt: 10 };
  applyWonFormat(ws, '판매가');
  ws['!autofilter'] = { ref: ws['!ref'] ?? 'A1' };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '번호 기입폼(통합)');

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const first = sorted[0];
  const ordererName = first?.receiverName ? `_${first.receiverName}` : '';
  saveAs(
    new Blob([out], { type: 'application/octet-stream' }),
    `발송번호기입폼_통합${ordererName}_주문${sorted.length}건.xlsx`,
  );
};
